import React from "react"
import { Box, Button, LocationIcon, Stack, SvgIcon, Typography } from "@carrotfertility/carotene-core"
import { FormattedMessage } from "react-intl"
import { Link, useLocation } from "react-router-dom"
import { CarrotClinicProvider } from "../../types/Providers"
import { VirtualServiceRestrictionNotice } from "./VirtualServiceRestrictionNotice"
import { Paths } from "#/utils/Paths"
import { useBookingActionTracking } from "#/services/memberActionTracking/BookingTrackingProvider"

export const CARROT_CLINIC_PROVIDER_UUID = -1

export function CarrotClinicCard({
  provider,
  stateCode
}: {
  provider: CarrotClinicProvider
  stateCode: string | null
}): JSX.Element {
  return (
    <Box
      sx={{
        display: "flex",
        inlineSize: "100%",
        justifyContent: "space-between",
        flexDirection: ["column", "row", "row", "row"]
      }}
      gap={(theme) => [
        theme.spacing(theme.tokens.spacing.md),
        theme.spacing(theme.tokens.spacing.sm),
        theme.spacing(theme.tokens.spacing.sm),
        theme.spacing(theme.tokens.spacing.md)
      ]}
    >
      <Stack gap={(theme) => theme.tokens.spacing.md}>
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-end",
            gap: theme.tokens.spacing.xxxs
          })}
        >
          <CarrotBrandMark />
          <Typography variant="h4" component="h2">
            {provider.providerName}
          </Typography>
        </Box>
        <Typography variant="body1">
          <FormattedMessage defaultMessage="Virtual visits with Carrot Clinicians." />
        </Typography>
        <Box>
          <BookButton program={provider.program} />
        </Box>
        {/* Skip when state is known — the BFF already gated on it. */}
        {!stateCode && (
          <VirtualServiceRestrictionNotice virtualServicesNotAvailableIn={provider.virtualServicesNotAvailableIn} />
        )}
      </Stack>
      <Box
        sx={{
          display: "flex",
          flexDirection: ["row", null, "column"],
          alignItems: "center",
          alignSelf: "stretch",
          justifyContent: "flex-start",
          minInlineSize: 90
        }}
        gap={(theme) => theme.spacing(theme.tokens.spacing.xxs)}
      >
        <Box
          component="span"
          sx={(theme) => ({
            color: theme.palette.text.tertiary,
            display: "flex",
            alignItems: "center"
          })}
          gap={(theme) => theme.spacing(theme.tokens.spacing.xxs)}
        >
          <LocationIcon fontSize="inherit" role="presentation" color="inherit" />
          <Typography variant="body2" color="inherit">
            <FormattedMessage defaultMessage="Virtual" />
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

function CarrotBrandMark(): JSX.Element {
  return (
    <SvgIcon fontSize="medium" color="primary" viewBox="0 0 32 32">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15.9419 8.81546L9.01584 25.0665H5.33337L14.3344 4.5332H17.6657L26.6667 25.0665H22.8679L15.9419 8.81546Z"
        fill="currentColor"
      />
    </SvgIcon>
  )
}

function BookButton({ program }: { program: CarrotClinicProvider["program"] }): JSX.Element {
  const { pathname, search } = useLocation()
  const { trackBookingEntry } = useBookingActionTracking()
  const destination = {
    pathname: Paths.TELEMEDICINE_BOOKING,
    search: new URLSearchParams({ program }).toString(),
    state: { returnTo: pathname + search }
  }
  return (
    <Button
      variant="outlined"
      color="secondary"
      component={Link}
      to={destination}
      onClick={(event) => trackBookingEntry(event, "provider_finder", program)}
    >
      <FormattedMessage defaultMessage="Book appointment" />
    </Button>
  )
}
